import {z} from 'zod';
import {
	editEffectsSortOrder,
	type EditEffectsSortOrderParams,
	getSortField,
	type GetSortFieldParams,
	getSortOrder,
	type GetSortOrderParams,
	SortOrder,
	SortSchema,
} from '@netx/core-schema';
import {
	SharedResourceField,
	type ISharedResource,
} from './sharedResourceSchema';

export const SHARED_RESOURCE_SORTABLE_FIELDS = [
	SharedResourceField.title,
	SharedResourceField.contentCount,
] as const;

export const SharedResourceSortSchema = SortSchema.omit({
	field: true,
}).extend({
	field: z.enum(SHARED_RESOURCE_SORTABLE_FIELDS),
});

export type SharedResourceSort = z.infer<typeof SharedResourceSortSchema>;

export const SharedResourceSortableField = z.enum(
	SHARED_RESOURCE_SORTABLE_FIELDS,
).enum;

export const SHARED_RESOURCE_DEFAULT_SORT_FIELD =
	SharedResourceSortableField.title;

export const SHARED_RESOURCE_DEFAULT_SORT_ORDER = SortOrder.ASCENDING;

/** will return the sort passed in if valid/specified, otherwise will return the defaults for the invalid/unspecified */
export const getSharedResourceDefaultSort = (
	sort: Partial<SharedResourceSort> = {},
): SharedResourceSort => ({
	field: getSharedResourceSortField({sortField: sort.field}),
	order: getSharedResourceSortOrder({sortOrder: sort.order}),
});

/** will return the passed in sort field if valid, otherwise will retutn the default sort field */
export const getSharedResourceSortField = (
	params: Omit<
		GetSortFieldParams<(typeof SHARED_RESOURCE_SORTABLE_FIELDS)[number]>,
		'defaultSortField' | 'sortableFields'
	>,
) =>
	getSortField({
		...params,
		sortableFields: SHARED_RESOURCE_SORTABLE_FIELDS,
		defaultSortField: SHARED_RESOURCE_DEFAULT_SORT_FIELD,
	});

/** will return the passed in sort order if valid, otherwise will return the default sort order */
export const getSharedResourceSortOrder = (
	params: Omit<GetSortOrderParams, 'defaultSortOrder'>,
) =>
	getSortOrder({
		...params,
		defaultSortOrder: SHARED_RESOURCE_DEFAULT_SORT_ORDER,
	});

/** given the original data, the new data, and the current sort, determine if the sort order was effected */
export const sharedResourceEditEffectsOrder = (
	params: Omit<
		EditEffectsSortOrderParams<
			ISharedResource,
			(typeof SHARED_RESOURCE_SORTABLE_FIELDS)[number]
		>,
		'sortableFields'
	>,
) =>
	editEffectsSortOrder({
		...params,
		sortableFields: SHARED_RESOURCE_SORTABLE_FIELDS,
	});
